"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"

export function Pagination({
  page,
  totalPages,
  "data-testid": dataTestid,
}: {
  page: number
  totalPages: number
  "data-testid"?: string
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set("page", newPage.toString())
    router.push(`${pathname}?${params.toString()}`)
  }

  const buildPages = (): (number | "...")[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    if (page <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages]
    }
    if (page >= totalPages - 3) {
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }
    return [1, "...", page - 1, page, page + 1, "...", totalPages]
  }

  const pages = buildPages()

  const base =
    "flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-sm transition"
  const active =
    "bg-cyan-50 font-semibold text-cyan-700 dark:bg-cyan-400/10 dark:text-cyan-400 cursor-default"
  const inactive =
    "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5"
  const disabled = "cursor-not-allowed opacity-40"

  return (
    <div
      className="flex items-center justify-center gap-1"
      data-testid={dataTestid}
    >
      <button
        onClick={() => handlePageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Попередня сторінка"
        className={`${base} ${page <= 1 ? disabled + " " + inactive : inactive}`}
      >
        ←
      </button>

      {pages.map((p, i) =>
        p === "..." ? (
          <span
            key={`ellipsis-${i}`}
            className="flex h-8 w-8 items-center justify-center text-sm text-slate-400 dark:text-slate-600"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => handlePageChange(p)}
            disabled={p === page}
            aria-current={p === page ? "page" : undefined}
            className={`${base} ${p === page ? active : inactive}`}
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => handlePageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Наступна сторінка"
        className={`${base} ${page >= totalPages ? disabled + " " + inactive : inactive}`}
      >
        →
      </button>
    </div>
  )
}
